import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FETCH_STATUS } from '../../types/enums/fetch-status.enum';
import { getModelsList, getModelDetails, createModel, updateModel, deleteModelDetails } from './model.action';
import snackbarUtils from '../../utils/SnackbarUtils';
import { SET_ROUTE_PARAMS } from '../../redux/constants';
import { ToolkitActionType } from '../../types/utils/toolkit-action.type';
import { RouteParamsType } from '../../types/utils/router-params.type';
import { ModelListResponse } from './model.types';
import { ModelType } from '../../types/inventory/inventory.types';

interface ModelsState {
  modelsList: ModelListResponse | null;
  modelsListFetchStatus: FETCH_STATUS;
  modelsRouteParams: RouteParamsType;
  modelDetails: ModelType | null;
  modelDetailsFetchStatus: FETCH_STATUS;
  modelDetailsCreateStatus: FETCH_STATUS;
  modelDetailsDeleteStatus: FETCH_STATUS;
  modelDetailsUpdateStatus: FETCH_STATUS;
}

export const initialModelsRouteParams: RouteParamsType = {
  page: 1,
  limit: 10,
  order: '-id',
  q: '',
};

const initialState: ModelsState = {
  modelsList: null,
  modelsListFetchStatus: FETCH_STATUS.NULL,
  modelsRouteParams: initialModelsRouteParams,
  modelDetails: null,
  modelDetailsFetchStatus: FETCH_STATUS.NULL,
  modelDetailsCreateStatus: FETCH_STATUS.NULL,
  modelDetailsDeleteStatus: FETCH_STATUS.NULL,
  modelDetailsUpdateStatus: FETCH_STATUS.NULL,
};

const modelsSlice = createSlice({
  name: 'models',
  initialState,
  reducers: {
    setModelsList: (state, action: PayloadAction<ModelListResponse>) => {
      state.modelsList = action.payload;
    },
    setModelsListFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.modelsListFetchStatus = action.payload;
    },
    setModelsRouteParams: (state, action: PayloadAction<Partial<RouteParamsType>>) => {
      state.modelsRouteParams = { ...state.modelsRouteParams, ...action.payload };
    },
    setModelDetails: (state, action: PayloadAction<ModelType | null>) => {
      state.modelDetails = action.payload;
    },
    setModelDetailsFetchStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.modelDetailsFetchStatus = action.payload;
    },
    setModelDetailsCreateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.modelDetailsCreateStatus = action.payload;
    },
    setModelDetailsUpdateStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.modelDetailsUpdateStatus = action.payload;
    },
    setModelDetailsDeleteStatus: (state, action: PayloadAction<FETCH_STATUS>) => {
      state.modelDetailsDeleteStatus = action.payload;
    },
    resetModelsState: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(SET_ROUTE_PARAMS, (state, action) => {
        const { payload } = action as ToolkitActionType<{ reducer: string; data: RouteParamsType }>;
        if (payload.reducer === 'models') {
          state.modelsRouteParams = { ...state.modelsRouteParams, ...payload.data };
        }
      })

      // Get Brands List
      .addCase(getModelsList.pending, state => {
        state.modelsListFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getModelsList.fulfilled, (state, action) => {
        state.modelsList = action.payload;
        state.modelsListFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getModelsList.rejected, state => {
        state.modelsListFetchStatus = FETCH_STATUS.REJECTED;
      })
      // Get Brand Details
      .addCase(getModelDetails.pending, state => {
        state.modelDetailsFetchStatus = FETCH_STATUS.PENDING;
      })
      .addCase(getModelDetails.fulfilled, (state, action) => {
        state.modelDetails = action.payload;
        state.modelDetailsFetchStatus = FETCH_STATUS.FULFILLED;
      })
      .addCase(getModelDetails.rejected, (state, action) => {
        state.modelDetailsFetchStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      // Create Model
      .addCase(createModel.fulfilled, (state, action) => {
        state.modelDetailsCreateStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Model ${action.payload?.name} created successfully`);
      })
      .addCase(createModel.rejected, (state, action) => {
        state.modelDetailsCreateStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error(action.payload);
      })
      // Update Model
      .addCase(updateModel.fulfilled, (state, action) => {
        if (state.modelDetails?.id === action.payload.id) {
          state.modelDetails = action.payload;
        }
        state.modelDetailsUpdateStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Model ${action.payload?.name} updated successfully`);
      })
      .addCase(updateModel.rejected, state => {
        state.modelDetailsUpdateStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error('Failed to update model');
      })
      // Delete Model
      .addCase(deleteModelDetails.pending, state => {
        state.modelDetailsDeleteStatus = FETCH_STATUS.PENDING;
      })
      .addCase(deleteModelDetails.fulfilled, (state, action) => {
        state.modelDetailsDeleteStatus = FETCH_STATUS.FULFILLED;
        snackbarUtils.success(`Model ${action.payload?.name} deleted successfully`);
      })
      .addCase(deleteModelDetails.rejected, state => {
        state.modelDetailsDeleteStatus = FETCH_STATUS.REJECTED;
        snackbarUtils.error('Failed to delete model');
      });
  },
});

export const {
  setModelsList,
  setModelsListFetchStatus,
  setModelsRouteParams,
  setModelDetails,
  setModelDetailsFetchStatus,
  setModelDetailsDeleteStatus,
  setModelDetailsCreateStatus,
  setModelDetailsUpdateStatus,
  resetModelsState,
} = modelsSlice.actions;

export default modelsSlice.reducer;
